import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Hash,
  RefreshCw,
  Info,
} from "lucide-react";

const FindRepeatingAndMissingVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Basic example",
      array: [3, 1, 2, 5, 3],
      result: { repeating: 3, missing: 4 },
    },
    {
      id: 2,
      name: "Larger array",
      array: [3, 1, 2, 5, 4, 6, 7, 5],
      result: { repeating: 5, missing: 8 },
    },
    {
      id: 3,
      name: "No-op needed",
      array: [1, 2, 3, 4, 5],
      result: { repeating: "N/A", missing: "N/A" }, // Edge case, problem implies one of each
    },
    {
      id: 4,
      name: "Repeating at start",
      array: [1, 1, 3, 4, 5],
      result: { repeating: 1, missing: 2 },
    },
    {
      id: 5,
      name: "Missing at start",
      array: [2, 3, 4, 5, 2],
      result: { repeating: 2, missing: 1 },
    },
    {
      id: 6,
      name: "Repeating at end",
      array: [1, 2, 3, 4, 4],
      result: { repeating: 4, missing: 5 },
    },
    {
      id: 7,
      name: "Unordered",
      array: [4, 5, 2, 1, 4],
      result: { repeating: 4, missing: 3 },
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [inputArray, setInputArray] = useState(testCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);
  const [found, setFound] = useState(null);

  // Algorithm approach
  const [approach, setApproach] = useState("cyclicSort"); // "frequency" or "cyclicSort"

  // Pseudo code for both approaches
  const frequencyPseudoCode = [
    { line: "function findErrorNums(nums):", indent: 0 },
    { line: "n = nums.length", indent: 1 },
    { line: "freq = new Array(n + 1).fill(0)", indent: 1 },
    { line: "", indent: 0 },
    { line: "for num in nums:", indent: 1 },
    { line: "freq[num]++", indent: 2 },
    { line: "", indent: 0 },
    { line: "repeating, missing = -1, -1", indent: 1 },
    { line: "for i = 1 to n:", indent: 1 },
    { line: "if freq[i] == 2:", indent: 2 },
    { line: "repeating = i", indent: 3 },
    { line: "if freq[i] == 0:", indent: 2 },
    { line: "missing = i", indent: 3 },
    { line: "", indent: 0 },
    { line: "return {repeating, missing}", indent: 1 },
  ];

  const cyclicSortPseudoCode = [
    { line: "function findErrorNums(nums):", indent: 0 },
    { line: "i = 0, n = nums.length", indent: 1 },
    { line: "while i < n:", indent: 1 },
    { line: "correctIndex = nums[i] - 1", indent: 2 },
    { line: "if nums[i] != nums[correctIndex]:", indent: 2 },
    { line: "swap(nums, i, correctIndex)", indent: 3 },
    { line: "else:", indent: 2 },
    { line: "i++", indent: 3 },
    { line: "", indent: 0 },
    { line: "for j = 0 to n-1:", indent: 1 },
    { line: "if nums[j] != j + 1:", indent: 2 },
    { line: "repeating = nums[j]", indent: 3 },
    { line: "missing = j + 1", indent: 3 },
    { line: "return {repeating, missing}", indent: 3 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate steps for Frequency Counter
  const calculateFrequencySteps = (nums) => {
    const n = nums.length;
    const allSteps = [];
    let stepCount = 0;

    // Initial state
    allSteps.push({
      step: stepCount++,
      array: [...nums],
      freq: new Array(n + 1).fill(0),
      pointers: {},
      description: "Start with the input array.",
      highlightLine: 0,
    });

    const freq = new Array(n + 1).fill(0);
    allSteps.push({
      step: stepCount++,
      array: [...nums],
      freq: [...freq],
      pointers: {},
      description: `Create a frequency counter of size N+1 (${n + 1}).`,
      highlightLine: 2,
    });

    // Populate frequency array
    for (let i = 0; i < n; i++) {
      const num = nums[i];
      allSteps.push({
        step: stepCount++,
        array: [...nums],
        freq: [...freq],
        pointers: { i },
        description: `Read element at index ${i}: ${num}.`,
        highlightLine: 4,
      });

      freq[num]++;
      allSteps.push({
        step: stepCount++,
        array: [...nums],
        freq: [...freq],
        pointers: { i, freqIndex: num },
        description: `Increment count for ${num}. Count is now ${freq[num]}.`,
        highlightLine: 5,
      });
    }

    // Find missing and repeating
    let repeating = -1,
      missing = -1;
    for (let i = 1; i <= n; i++) {
      allSteps.push({
        step: stepCount++,
        array: [...nums],
        freq: [...freq],
        pointers: { scanIndex: i },
        description: `Check frequency of number ${i}.`,
        highlightLine: 8,
      });

      if (freq[i] === 2) {
        repeating = i;
        allSteps.push({
          step: stepCount++,
          array: [...nums],
          freq: [...freq],
          pointers: { scanIndex: i },
          description: `Frequency is 2. ${i} is the repeating number.`,
          highlightLine: 9,
          found: { repeating: i },
        });
      }
      if (freq[i] === 0) {
        missing = i;
        allSteps.push({
          step: stepCount++,
          array: [...nums],
          freq: [...freq],
          pointers: { scanIndex: i },
          description: `Frequency is 0. ${i} is the missing number.`,
          highlightLine: 11,
          found: { repeating, missing: i },
        });
      }
    }

    // Final state
    allSteps.push({
      step: stepCount++,
      array: [...nums],
      freq: [...freq],
      description: `Scan complete. Repeating: ${repeating}, Missing: ${missing}.`,
      highlightLine: 14,
      isFinal: true,
      found: { repeating, missing },
    });

    return allSteps;
  };

  // Calculate steps for Cyclic Sort
  const calculateCyclicSortSteps = (nums) => {
    const n = nums.length;
    const allSteps = [];
    let stepCount = 0;
    let currentNums = [...nums];

    // Initial state
    allSteps.push({
      step: stepCount++,
      array: [...currentNums],
      pointers: { i: 0 },
      description: "Start Cyclic Sort to place numbers in correct positions.",
      highlightLine: 2,
    });

    let i = 0;
    while (i < n) {
      const correctIndex = currentNums[i] - 1;
      allSteps.push({
        step: stepCount++,
        array: [...currentNums],
        pointers: { i, correctIndex },
        description: `At index ${i}, value is ${currentNums[i]}. Correct index should be ${correctIndex}.`,
        highlightLine: 3,
      });

      if (
        correctIndex >= 0 &&
        correctIndex < n &&
        currentNums[i] !== currentNums[correctIndex]
      ) {
        allSteps.push({
          step: stepCount++,
          array: [...currentNums],
          pointers: { i, correctIndex },
          description: `Value ${currentNums[i]} is not at correct index. Swap with value ${currentNums[correctIndex]}.`,
          highlightLine: 4,
          swapping: [i, correctIndex],
        });
        [currentNums[i], currentNums[correctIndex]] = [
          currentNums[correctIndex],
          currentNums[i],
        ];
        allSteps.push({
          step: stepCount++,
          array: [...currentNums],
          pointers: { i, correctIndex },
          description: "Swap complete.",
          highlightLine: 5,
        });
      } else {
        allSteps.push({
          step: stepCount++,
          array: [...currentNums],
          pointers: { i, correctIndex },
          description: `Value ${currentNums[i]} is already in place or points to a duplicate. Move to next index.`,
          highlightLine: 7,
        });
        i++;
      }
    }

    allSteps.push({
      step: stepCount++,
      array: [...currentNums],
      pointers: {},
      description: "Placement phase complete. Now scan for the error.",
      highlightLine: 9,
    });

    // Find missing and repeating
    let repeating = -1,
      missing = -1;
    for (let j = 0; j < n; j++) {
      allSteps.push({
        step: stepCount++,
        array: [...currentNums],
        pointers: { j },
        description: `Check index ${j}. Expected value is ${j + 1}.`,
        highlightLine: 10,
      });
      if (currentNums[j] !== j + 1) {
        repeating = currentNums[j];
        missing = j + 1;
        allSteps.push({
          step: stepCount++,
          array: [...currentNums],
          pointers: { j },
          description: `Error found! Value is ${repeating}, should be ${missing}.`,
          highlightLine: 11,
          found: { repeating, missing },
        });
        break;
      }
    }

    // Final state
    allSteps.push({
      step: stepCount++,
      array: [...currentNums],
      description: `Scan complete. Repeating: ${repeating}, Missing: ${missing}.`,
      highlightLine: 13,
      isFinal: true,
      found: { repeating, missing },
    });

    return allSteps;
  };

  // Initialize steps when test case or approach changes
  useEffect(() => {
    const newSteps =
      approach === "frequency"
        ? calculateFrequencySteps(inputArray)
        : calculateCyclicSortSteps(inputArray);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setFound(null);
  }, [inputArray, approach]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      const step = steps[nextStep];
      setCurrentStep(nextStep);
      setHighlightedLine(step.highlightLine);
      if (step.found) setFound(step.found);
      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const step = steps[prevStep];
      setCurrentStep(prevStep);
      setHighlightedLine(step.highlightLine);
      setFinished(false);
      if (step.found) setFound(step.found);
      else setFound(null);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
    setFound(null);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setInputArray(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(customInput);
      if (!Array.isArray(nums) || !nums.every((n) => typeof n === "number")) {
        throw new Error("Invalid array format.");
      }

      // Simple way to find result for custom case
      const n = nums.length;
      const numSet = new Set();
      let repeating = -1;
      let sum = 0;
      for (const num of nums) {
        if (numSet.has(num)) repeating = num;
        numSet.add(num);
        sum += num;
      }
      const expectedSum = (n * (n + 1)) / 2;
      const missing = expectedSum - (sum - repeating);

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 6}`,
        array: nums,
        result: { repeating, missing },
      };

      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert("Invalid format. Use a valid JSON array like [3,1,2,5,3]");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <RefreshCw className="text-blue-500" size={24} />
        Find Repeating & Missing Number
      </h2>

      {/* Problem Statement */}
      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1 flex-shrink-0" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">Problem</h3>
              <p className="text-sm text-blue-800 mb-2">
                Given a read-only array of N integers from 1 to N, every number
                appears exactly once except one number 'A' which appears twice,
                and one number 'B' which is missing. Find A and B.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Approach Selector */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Select Approach</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setApproach("cyclicSort")}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "cyclicSort"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Cyclic Sort (Optimal)
              </button>
              <button
                onClick={() => setApproach("frequency")}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "frequency"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Frequency Counter
              </button>
            </div>
          </div>

          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 truncate">
                      Input: [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 7 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom array: [3,1,2,5,3]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          {/* Visualization Area */}
          <div className="bg-white p-3 rounded-lg shadow min-h-[250px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Main Array */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-1">
                {approach === "cyclicSort" &&
                currentStepData?.highlightLine >= 9
                  ? "Sorted Array:"
                  : "Input Array:"}
              </p>
              <div className="flex gap-1 flex-wrap items-center">
                {currentStepData?.array.map((val, idx) => (
                  <div key={idx} className="relative text-center">
                    <div
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 font-mono text-sm font-bold ${
                        currentStepData.pointers?.i === idx ||
                        currentStepData.pointers?.j === idx
                          ? "bg-blue-200 border-blue-500 scale-110"
                          : currentStepData.pointers?.correctIndex === idx
                          ? "bg-yellow-200 border-yellow-500"
                          : currentStepData.swapping?.includes(idx)
                          ? "bg-purple-300 border-purple-500 animate-pulse"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      {val}
                    </div>
                    <span className="text-xs text-gray-500 mt-1">{idx}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Frequency Array (if applicable) */}
            {approach === "frequency" && currentStepData?.freq && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Frequency Counter:
                </p>
                <div className="flex gap-1 flex-wrap items-center">
                  {currentStepData.freq.map((val, idx) =>
                    idx > 0 ? ( // Skip index 0
                      <div key={idx} className="relative text-center">
                        <div
                          className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 font-mono text-sm ${
                            currentStepData.pointers?.freqIndex === idx ||
                            currentStepData.pointers?.scanIndex === idx
                              ? "bg-green-200 border-green-500 scale-110"
                              : "bg-gray-100 border-gray-300"
                          }`}
                        >
                          {val}
                        </div>
                        <span className="text-xs text-gray-500 mt-1">
                          {idx}
                        </span>
                      </div>
                    ) : null
                  )}
                </div>
              </div>
            )}

            {/* Result */}
            {finished && found && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">
                  Result Found!
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  Repeating Number (A):{" "}
                  <span className="font-bold">{found.repeating}</span>
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Missing Number (B):{" "}
                  <span className="font-bold">{found.missing}</span>
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {(approach === "frequency"
                ? frequencyPseudoCode
                : cyclicSortPseudoCode
              ).map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <div
              className={`p-2 rounded ${
                approach === "cyclicSort" ? "bg-blue-50" : "bg-gray-50"
              }`}
            >
              <h4 className="text-xs font-medium text-gray-700 mb-1">
                Cyclic Sort
              </h4>
              <p className="text-xs text-gray-600">
                Use the array itself to place numbers at their correct index
                (e.g., number `k` at index `k-1`). One pass to sort, one pass to
                find the discrepancy.
              </p>
              <div className="flex justify-around mt-1 text-xs">
                <div>
                  <span className="font-medium">Time:</span> O(N)
                </div>
                <div>
                  <span className="font-medium">Space:</span> O(1)
                </div>
              </div>
            </div>
            <div
              className={`p-2 rounded mt-2 ${
                approach === "frequency" ? "bg-blue-50" : "bg-gray-50"
              }`}
            >
              <h4 className="text-xs font-medium text-gray-700 mb-1">
                Frequency Counter
              </h4>
              <p className="text-xs text-gray-600">
                Use an auxiliary array (or hash map) to count occurrences of
                each number. One pass to count, one pass to find counts of 0 and
                2.
              </p>
              <div className="flex justify-around mt-1 text-xs">
                <div>
                  <span className="font-medium">Time:</span> O(N)
                </div>
                <div>
                  <span className="font-medium">Space:</span> O(N)
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The range of numbers [1, N] is a hint to use the array indices
                  for tracking.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Cyclic Sort works because after sorting, the number `k` should
                  be at index `k-1`. The misplaced number reveals both answers.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The problem asks for a read-only array, so technically
                  modifying it (Cyclic Sort) isn't allowed. We visualize it as
                  if we have a mutable copy.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The frequency method is a straightforward trade-off: use more
                  memory for a simpler logic.
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-200 border-2 border-blue-500 rounded"></div>
                <span>Current Pointer (i or j)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-yellow-200 border-2 border-yellow-500 rounded"></div>
                <span>Correct/Target Index</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-300 border-2 border-purple-500 rounded"></div>
                <span>Elements being swapped</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-200 border-2 border-green-500 rounded"></div>
                <span>Frequency Index being checked</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindRepeatingAndMissingVisualizer;
